import { useEffect, useRef, useState } from 'react';
import * as S from './styles';
import { UploadButton } from 'components/Buttons/UploadButton';
import cloudIcon from 'assets/image/cloudIcon.svg';
import { useApi } from "hooks/useApis";
import JSZip from "jszip";
import { CardUpdateAlert } from 'components/CadUpdateAlert';
import { CadUpdateStatusCard } from 'components/CadUpdateStatusCard';

const successMsg = "A Base do CadÚnico começou a ser atualizada! Verifique o progresso abaixo.";
const errorMsg = "Ocorreu um erro ao atualizar a base. Tente novamente mais tarde.";

const FileUpload = ({ setSuccessMessage, setErrorMessage, resetFile }) => {
  const api = useApi();
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [isValidatingZip, setIsValidatingZip] = useState(false);

  const [getLastCadTaskTrigger, setGetLastCadTaskTrigger] = useState(0);
  const [lastCadTask, setLastCadTask] = useState(null);
  const [isImportingCad, setIsImportingCad] = useState(false);

  const intervalId = useRef();

  useEffect(() => {
    api.getLastCadImporterTask()
      .then((data) => {
        setLastCadTask(data.detail);
        const { status } = data.detail;

        // we should only show the modal if an upload was started in this session
        if (isImportingCad) {
          if (status === 'SUCCESS') {
            setSuccessMessage(successMsg);
          } else if (status === 'FAILED') {
            setErrorMessage(errorMsg);
          }
        }

        // success or failure are the conditions to stop fetching lastTask
        if (status === 'SUCCESS' || status === 'FAILED') {
          clearInterval(intervalId.current);
          intervalId.current = null;
          if (isImportingCad) {
            setIsUploading(false);
            setUploadCompleted(true);
            setIsImportingCad(false);
          }
        }
      })
      .catch((err) => console.warn(err))
  }, [getLastCadTaskTrigger, isImportingCad])


  const startFetchingLastTask = () => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }

    setIsImportingCad(true);
    intervalId.current = setInterval(() => {
      setGetLastCadTaskTrigger(Date.now());
    }, 5000)

  }

  const validateZipContents = async (uploadedFile) => {
    try {
      setIsValidatingZip(true);
      const zip = await JSZip.loadAsync(uploadedFile);
      const filesInsideZip = Object.keys(zip.files);

      const foundCsv = filesInsideZip.some(fileName => fileName === 'tab_cad.csv');

      setIsValidatingZip(false);

      if (!foundCsv) {
        throw new Error(
          `O arquivo 'tab_cad.csv' não foi encontrado dentro do ZIP. Por favor, renomeie o arquivo dentro do ZIP e tente novamente.`
        );
      }

      return true;
    } catch (error) {
      setErrorMessage(error.message || "Erro ao verificar o conteúdo do ZIP.");
      setIsValidatingZip(false);
      return false;
    }
  };

  const handleFileChange = async (uploadedFile) => {
    if (!uploadedFile.name.endsWith(".zip")) {
      setErrorMessage("Apenas arquivos ZIP são permitidos.");
      resetFile();
      return;
    }

    const isValidZip = await validateZipContents(uploadedFile);
    if (!isValidZip) {
      resetFile();
      return;
    }

    setFile(uploadedFile);
    setSuccessMessage("");
    setErrorMessage("");
    setIsUploading(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);

    const uploadedFile = event.dataTransfer.files[0];
    if (uploadedFile) {
      handleFileChange(uploadedFile);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };


  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleUpload = async () => {
    if (!file) {
      setErrorMessage("Nenhum arquivo selecionado.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadCompleted(false);

      await api.uploadCadFile(file);

      // We should start fetching the last task status now
      startFetchingLastTask();
    } catch (error) {
      setErrorMessage(error || "Erro desconhecido ao enviar o arquivo.");
      setIsUploading(false);
      setUploadCompleted(false);
    }
  };

  return (
    <S.Container>
      <S.UploadBox
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        isDragOver={isDragOver}
      >
        <S.IconContainer>
          <img src={cloudIcon} alt="Cloud Icon" />
        </S.IconContainer>
        <S.InstructionText>Arraste e solte os arquivos para enviar ou</S.InstructionText>
        <S.SelectButton htmlFor="file-input">Selecione aqui</S.SelectButton>
        <input
          id="file-input"
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => handleFileChange(e.target.files[0])}
        />
        {file && <S.FileName>{file.name}</S.FileName>}
        <S.SupportFormats>Formatos suportados: ZIP (contendo tab_cad.csv).</S.SupportFormats>
      </S.UploadBox>


      {uploadCompleted && !isUploading && !isValidatingZip && (
        <S.InstructionSubText>Upload Concluído</S.InstructionSubText>
      )}

      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        marginTop: "1rem"
      }}>
        <div style={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "flex-end"
        }}>
          <UploadButton onClick={handleUpload} />
        </div>

        {(isValidatingZip || isUploading) && (
          <div style={{
            marginTop: "1rem",
          }}>
            <CardUpdateAlert />
          </div>
        )}

        <div style={{
          width: "100%",
          height: "100%"
        }}>
          <CadUpdateStatusCard cadTask={lastCadTask} />
        </div>
      </div>

    </S.Container>
  );
};

export default FileUpload;
